import { useCallback, useEffect, useRef, useState } from 'react'
import { GripHorizontal } from 'lucide-react'

import { useAppStore } from '@core/store'
import { useJanelaAberta } from '@core/layout/useJanelaAberta'
import JanelaDestacada, {
  BotaoDestacar,
} from '@core/layout/janela-destacada/JanelaDestacada'
import PainelDados from '@viewer/dados/PainelDados'

/** A janela flutuante que segura o painel Dados do IFC.
 *
 *  Mesmo padrão das outras: arrasta-se pela barra de título e fica
 *  onde a pessoa a puser. O conteúdo vive em
 *  `@viewer/dados/PainelDados`, que não sabe nada de janelas. */
export default function FloatingDadosPanel() {
  const open = useAppStore((s) => s.dadosPanelOpen)
  const setOpen = useAppStore((s) => s.setDadosPanelOpen)
  const { z, trazer } = useJanelaAberta('dados', open)
  const [position, setPosition] = useState({ x: 380, y: 120 })
  // Destacado numa janela do browser (a tabela num segundo ecrã, o
  // modelo no primeiro). O "Abrir no viewer" e as cores continuam a ir
  // para o 3D da janela principal.
  const [destacada, setDestacada] = useState(false)
  const voltar = useCallback(() => setDestacada(false), [])
  const fechar = useCallback(() => {
    setDestacada(false)
    setOpen(false)
  }, [setOpen])
  const dragRef = useRef<{
    pointerStart: { x: number; y: number }
    posStart: { x: number; y: number }
  } | null>(null)

  const onTitleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      dragRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        posStart: { ...position },
      }
      try {
        ;(e.target as Element).setPointerCapture?.(e.pointerId)
      } catch {
        // Um alvo que já saiu do DOM atira aqui; arrastar continua a
        // funcionar pelos ouvintes da janela.
      }
    },
    [position],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current
      if (!d) return
      setPosition({
        x: d.posStart.x + (e.clientX - d.pointerStart.x),
        y: d.posStart.y + (e.clientY - d.pointerStart.y),
      })
    }
    const onUp = () => {
      dragRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [])

  if (!open) return null

  if (destacada) {
    return (
      <JanelaDestacada
        aberta
        nome="vn2r-dados"
        titulo="Dados do IFC"
        features="width=900,height=820,resizable=yes,scrollbars=yes"
        oQue="os Dados do IFC"
        aoVoltar={voltar}
      >
        <PainelDados aoFechar={fechar} />
      </JanelaDestacada>
    )
  }

  return (
    <div
      onPointerDownCapture={trazer}
      className="fixed flex h-[72vh] w-[680px] max-w-[calc(100vw-16px)] flex-col rounded border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl"
      style={{ zIndex: z, left: position.x, top: position.y }}
    >
      <div
        onPointerDown={onTitleDown}
        className="relative flex cursor-move items-center justify-center border-b border-[var(--color-border)] py-1 text-[var(--color-text-dim)]"
      >
        <GripHorizontal className="h-3 w-3" aria-hidden />
        <span
          className="absolute right-1 top-0.5"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <BotaoDestacar
            onClick={() => setDestacada(true)}
            rotulo="Destacar os dados do IFC para uma janela do browser"
            tamanho={11}
          />
        </span>
      </div>
      <PainelDados aoFechar={() => setOpen(false)} />
    </div>
  )
}
